<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Edit Product</h5>
          <p class="page-sub">Update product details and pricing</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="form-card">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 6" :key="n"></div>
        </div>
      </div>
  
      <form v-else @submit.prevent="editProduct">
  
        <!-- Basic Info -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="box" />
            </div>
            <div>
              <p class="section-title">Product Information</p>
              <p class="section-sub">Update name, barcode and unit of measurement</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Bar Code</label>
              <div class="input-icon-wrap">
                <font-awesome-icon icon="barcode" class="input-icon" />
                <input v-model="bar_code" type="text" class="field-input field-input--icon" placeholder="Scan or enter barcode" />
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Product Name <span class="req">*</span></label>
              <input v-model="product_name" type="text" class="field-input" placeholder="Enter product name" />
            </div>
            <div class="field-group">
              <label class="field-label">Unit of Measurement <span class="req">*</span></label>
              <Multiselect
                v-model="unit_of_measurement"
                :options="UOM"
                label="name"
                placeholder="Select unit"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                class="custom-multiselect"
              />
            </div>
          </div>
        </div>
  
        <!-- Pricing -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--green">
              <font-awesome-icon icon="dollar-sign" />
            </div>
            <div>
              <p class="section-title">Pricing</p>
              <p class="section-sub">Set cost price, markup and selling price</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Cost Price</label>
              <input v-model="cost_price" type="text" class="field-input"
                placeholder="0.00"
                oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                @keyup="handleInput" />
            </div>
            <div class="field-group">
              <label class="field-label">Mark Up (%)</label>
              <input v-model="markup" type="number" min="0" class="field-input"
                placeholder="0"
                onkeypress="return /^[0-9]+$/i.test(event.key)"
                @keyup="handleInput" />
              <span class="field-error" v-if="markup_error">{{ markup_error }}</span>
            </div>
            <div class="field-group">
              <label class="field-label">Selling Price</label>
              <input v-model="selling_price" type="text" class="field-input"
                placeholder="0.00"
                oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                :disabled="isDisable" />
              <span class="field-hint" v-if="isDisable">Auto-calculated from cost + markup</span>
            </div>
          </div>
        </div>
  
        <!-- Category & Stock -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--purple">
              <font-awesome-icon icon="tags" />
            </div>
            <div>
              <p class="section-title">Category & Stock</p>
              <p class="section-sub">Assign category and manage stock levels</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Category</label>
              <select class="field-input field-select" v-model="category" @change="categorySub($event)">
                <option value="" disabled>Choose category</option>
                <option v-for="cat in allCategories" :key="cat.id" :value="cat.id">{{ cat.category_name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Subcategory</label>
              <select class="field-input field-select" v-model="subcategory">
                <option v-if="dropDownLoading" disabled>Loading...</option>
                <option value="">-- Choose --</option>
                <option v-for="sub in allSubcategories" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Warning Quantity</label>
              <input v-model="warning_quantity" type="number" class="field-input"
                placeholder="e.g. 10"
                onkeypress="return /^[0-9]+$/i.test(event.key)" />
              <span class="field-error" v-if="warningQty_error">{{ warningQty_error }}</span>
            </div>
            <div class="field-group fields-col-full">
              <label class="field-label">Quantity</label>
              <input type="text" v-model="quantity" disabled class="field-input" placeholder="—" />
              <span class="field-hint">Purchase product to update quantity</span>
            </div>
          </div>
        </div>
  
        <!-- Tax -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--orange">
              <font-awesome-icon icon="percent" />
            </div>
            <div>
              <p class="section-title">Tax</p>
              <p class="section-sub">Select taxes that apply to this product</p>
            </div>
          </div>
  
          <div v-if="taxes.length" class="tax-grid">
            <label v-for="tax in taxes" :key="tax.id" class="tax-item">
              <input
                type="checkbox"
                class="tax-check"
                :value="tax.id"
                v-model="tax_id"
                :checked="check(tax.id)"
              />
              <div class="tax-info">
                <span class="tax-name">{{ tax.tax_name }}</span>
                <span class="tax-rate">{{ tax.tax_percentage }}%</span>
              </div>
            </label>
          </div>
          <p v-else class="text-muted" style="font-size:13px">No taxes configured.</p>
        </div>
  
        <!-- Variants -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="layer-group" />
            </div>
            <div>
              <p class="section-title">Price Variants</p>
              <p class="section-sub">Add variants if this product is sold in multiple versions (e.g. pieces, dozens, cartons). Store quantity in pieces.</p>
            </div>
          </div>
  
          <div v-for="(vary, index) in variants" :key="index" class="variant-row">
            <div class="fields-grid fields-grid--variant">
              <div class="field-group">
                <label class="field-label">Variant Name</label>
                <input v-model="vary.variant_name" type="text" class="field-input" placeholder="e.g. Carton" />
              </div>
              <div class="field-group">
                <label class="field-label">Variant Price</label>
                <input v-model="vary.variant_selling_price" type="text" class="field-input"
                  placeholder="0.00"
                  oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');" />
              </div>
              <div class="field-group">
                <label class="field-label">Quantity per Unit</label>
                <input v-model="vary.variant_quantity" type="number" class="field-input"
                  placeholder="e.g. 12"
                  onkeypress="return /^[0-9]+$/i.test(event.key)" />
              </div>
              <div class="field-group variant-delete">
                <label class="field-label">&nbsp;</label>
                <button @click.prevent="removeVariant(index)" class="btn-remove-variant" title="Remove variant">
                  <font-awesome-icon icon="times" />
                </button>
              </div>
            </div>
          </div>
  
          <button @click.prevent="addVariant" class="btn-add-variant">
            <font-awesome-icon icon="plus" class="me-2" />
            Add Variant
          </button>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary-action" :disabled="crudLoading">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ crudLoading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
  
      </form>
    </div>
  </template>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <script>
  import { onMounted, ref } from "@vue/runtime-core";
  import { useRoute, useRouter } from "vue-router";
  import InventoryHeaderTab from "./component/inventoryheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import host from "../../../main";
  import Multiselect from '@vueform/multiselect';
  
  export default {
    components: { InventoryHeaderTab, Multiselect },
  
    setup() {
      const { params } = useRoute();
      const product              = ref('');
      const product_name         = ref('');
      const cost_price           = ref('');
      const selling_price        = ref('');
      const category             = ref('');
      const subcategory          = ref('');
      const bar_code             = ref('');
      const unit_of_measurement  = ref('');
      const markup               = ref('');
      const quantity             = ref('');
      const max_warning_quantity = ref('');
      const warning_quantity     = ref('');
      const variant_name         = ref('');
      const variant_selling_price = ref('');
      const variant_quantity     = ref('');
      const UOM                  = ref([]);
      const image                = ref(null);
      const new_image            = ref('');
      const variants             = ref([{ variant_name:'', variant_selling_price:'', variant_quantity:'' }]);
      const toast                = useToast();
      const router               = useRouter();
      const validationErrors     = ref([]);
      const allCategories        = ref([]);
      const allSubcategories     = ref([]);
      const dropDownLoading      = ref(false);
      const loading              = ref(false);
      const store                = ref({});
      const stores               = ref({});
      const store_arr            = ref([]);
      const taxes                = ref([]);
      const tax_id               = ref([]);
      const isDisable            = ref(false);
      const markup_error         = ref('');
      const warningQty_error     = ref('');
      const crudLoading          = ref(false);
      const previousTax          = ref([]);
  
      const check = (id) => previousTax.value.some(t => t.id == id);
  
      const handleInput = () => {
        markup_error.value     = Math.sign(markup.value) === -1 ? 'Markup must not be less than zero' : '';
        warningQty_error.value = Math.sign(warning_quantity.value) === -1 ? 'Warning quantity must not be less than zero' : '';
        if (cost_price.value && markup.value) {
          selling_price.value = markup.value == 0
            ? product.value.selling_price
            : ((parseInt(markup.value) / 100) * parseInt(cost_price.value)) + parseInt(cost_price.value);
        } else {
          isDisable.value = false;
        }
      };
  
      const categorySub = async (event) => {
        dropDownLoading.value = true;
        axios.post('/settings/category/subcategory', { category_id: event.target.value })
          .then(res => { allSubcategories.value = res.data.data; dropDownLoading.value = false; })
          .catch(() => { dropDownLoading.value = false; toast.error('An error occurred, please try again'); });
      };
  
      const getStores = async () => {
        const res = await axios.get('/admin/store');
        stores.value = res.data.data;
        store_arr.value = stores.value.map(s => ({ label: s.store_name, value: s.id }));
      };
  
      const addVariant    = () => variants.value.push({ variant_name:'', variant_selling_price:'', variant_quantity:'' });
      const removeVariant = (index) => variants.value.splice(index, 1);
  
      const getUnitofMeasurement = async () => {
        const res = await axios.get('/inventory/product/uom/unit-of-measurement');
        UOM.value = res.data.data.map(u => ({ value: u.id, name: u.name }));
      };
  
      const onFileSelected = (event) => {
        const file = event.target.files[0];
        if (file.size > 5048770) { toast.warning('Image too large'); return; }
        const reader = new FileReader();
        reader.onload = (e) => { new_image.value = e.target.result; };
        reader.readAsDataURL(file);
      };
  
      const editProduct = async () => {
        crudLoading.value = true;
        axios.put(`/inventory/product/web/${params.id}`, {
          store: store.value.value,
          tax_id: tax_id.value.filter(x => x !== null) ?? null,
          image: new_image.value,
          product_name: product_name.value,
          cost_price: cost_price.value,
          selling_price: selling_price.value,
          category: category.value,
          subcategory: subcategory.value,
          bar_code: bar_code.value,
          unit_of_measurement: unit_of_measurement.value,
          markup: markup.value,
          max_warning_quantity: max_warning_quantity.value,
          warning_quantity: warning_quantity.value,
          variant: variants.value,
          variant_name: variant_name.value,
          variant_selling_price: variant_selling_price.value,
          variant_quantity: variant_quantity.value,
        }).then(res => {
          crudLoading.value = false;
          router.push('/all-products');
          toast.success(res.data.message);
        }).catch(error => {
          crudLoading.value = false;
          if (error.response.status == 422) { toast.warning('There are errors in your input'); validationErrors.value = Array.isArray(error.response.data.message) ? error.response.data.message : (error.response.data.message ? [String(error.response.data.message)] : []); }
          if (error.response.status == 403) toast.error(error.response.data.message);
          if (error.response.status == 400) toast.error('Bad request, please check your input');
          if (error.response.status == 500) toast.error('An error occurred, please try again');
        });
      };
  
      const getProduct = async () => {
        axios.get(`/inventory/product/${params.id}`).then(response => {
          const p         = response.data.data;
          product.value   = p;
          loading.value   = false;
          variants.value  = p.variants;
          image.value     = p.image;
          bar_code.value  = p.bar_code;
          product_name.value        = p.product_name;
          quantity.value            = p.quantity;
          cost_price.value          = p.cost_price;
          markup.value              = p.markup;
          selling_price.value       = p.selling_price;
          category.value            = p.category;
          subcategory.value         = p.subcategory;
          unit_of_measurement.value = p?.uom?.id;
          warning_quantity.value    = p.warning_quantity;
          p.tax.forEach(t => { previousTax.value.push(t); tax_id.value.push(t.id); });
        }).catch(() => { loading.value = false; toast.error('An error occurred, please try again'); });
      };
  
      const getAllSubcategories = async () => {
        const res = await axios.get('settings/subcategory/all');
        allSubcategories.value = res.data.data;
      };
  
      onMounted(async () => {
        loading.value = true;
        getStores();
        getUnitofMeasurement();
        getAllSubcategories();
        const [taxRes, catRes] = await Promise.all([
          axios.get('settings/tax'),
          axios.get('/settings/category/all'),
        ]);
        taxes.value       = taxRes.data.data;
        allCategories.value = catRes.data.data;
        getProduct();
        if (cost_price.value && markup.value) isDisable.value = true;
      });
  
      return {
        crudLoading, categorySub, allSubcategories, subcategory, dropDownLoading,
        product, product_name, cost_price, selling_price, category, bar_code,
        unit_of_measurement, markup, max_warning_quantity, warning_quantity,
        variants, editProduct, addVariant, variant_name, variant_selling_price,
        variant_quantity, quantity, removeVariant, image, validationErrors,
        allCategories, loading, host, onFileSelected, new_image,
        store, stores, store_arr, taxes, handleInput, tax_id, check,
        previousTax, isDisable, warningQty_error, markup_error, UOM, getUnitofMeasurement,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Error Banner ── */
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  /* ── Skeleton ── */
  .skeleton-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; padding:8px 0; }
  .skeleton { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
  .section-icon--green  { background:rgba(22,163,74,.1);  color:#16a34a; }
  .section-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
  .section-icon--orange { background:rgba(224,140,0,.1);  color:#e08c00; }
  .section-icon--teal   { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Fields ── */
  .fields-grid     { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .fields-col-full { grid-column:1 / -1; }
  .field-group     { display:flex; flex-direction:column; gap:6px; }
  .field-label     { font-size:13px; font-weight:600; color:#1a2b45; }
  .req             { color:#e03535; }
  .field-error     { font-size:11.5px; color:#e03535; }
  .field-hint      { font-size:11.5px; color:#7a90b0; }
  
  .field-input {
    padding:9px 12px; border:1px solid #dde5f0; border-radius:8px;
    font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%;
    transition:border-color .15s, box-shadow .15s; font-family:inherit;
  }
  .field-input:focus   { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input::placeholder { color:#aab8cc; }
  .field-input:disabled { background:#f8fafc; color:#aab8cc; cursor:not-allowed; }
  .field-input--icon   { padding-left:36px; }
  .field-select { appearance:auto; cursor:pointer; }
  
  /* Barcode input */
  .input-icon-wrap { position:relative; }
  .input-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:14px; pointer-events:none; }
  
  /* Multiselect */
  :deep(.custom-multiselect) {
    --ms-border-color:#dde5f0; --ms-border-radius:8px;
    --ms-font-size:13.5px; --ms-tag-bg:#edf4ff; --ms-tag-color:#004AAD;
    --ms-ring-color:rgba(0,74,173,.08);
  }
  :deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
  
  /* ── Tax Grid ── */
  .tax-grid { display:flex; flex-wrap:wrap; gap:10px; }
  .tax-item {
    display:flex; align-items:center; gap:10px;
    background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px;
    padding:10px 14px; cursor:pointer; transition:border-color .15s, background .15s;
  }
  .tax-item:has(.tax-check:checked) { background:#edf4ff; border-color:#c5d6f5; }
  .tax-check { accent-color:#004AAD; width:15px; height:15px; cursor:pointer; flex-shrink:0; }
  .tax-name  { font-size:13px; font-weight:600; color:#1a2b45; }
  .tax-rate  { font-size:12px; color:#004AAD; background:rgba(0,74,173,.1); border-radius:20px; padding:1px 8px; margin-left:6px; }
  
  /* ── Variants ── */
  .variant-row { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:16px; margin-bottom:10px; }
  .fields-grid--variant { grid-template-columns:1fr 1fr 1fr 44px; }
  .variant-delete { justify-content:flex-end; }
  
  .btn-remove-variant {
    width:36px; height:36px; border-radius:8px;
    background:#fff0f0; border:1px solid #fac5c5;
    color:#e03535; display:flex; align-items:center; justify-content:center;
    cursor:pointer; transition:background .15s; flex-shrink:0;
  }
  .btn-remove-variant:hover { background:#ffe0e0; }
  
  .btn-add-variant {
    display:inline-flex; align-items:center;
    background:#edf4ff; color:#004AAD;
    border:1px solid #c5d6f5; font-size:13px; font-weight:600;
    padding:8px 18px; border-radius:8px; cursor:pointer;
    transition:background .15s; margin-top:4px;
  }
  .btn-add-variant:hover { background:#d6e4fa; }
  
  /* ── Footer ── */
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; padding-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 24px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .fields-grid { grid-template-columns:repeat(2,1fr); } .fields-grid--variant { grid-template-columns:1fr 1fr; } }
  @media(max-width:576px) {
    .fields-grid { grid-template-columns:1fr; }
    .fields-grid--variant { grid-template-columns:1fr; }
    .form-card { padding:16px; }
    .form-footer { flex-direction:column; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
  }
  </style>